// src/core/layer.js
import { select } from 'd3-selection'
import { getRole } from './layer-registry'
import * as Legend from '../legend/legend'

/** @typedef {import('../types/core/MapInstance').MapInstance} MapInstance */
/** @typedef {import('../types/core/layer/Layer').Layer} Layer */
/** @typedef {import('../types/core/layer/LayerConfig').LayerConfig} LayerConfig */

let _layerSeq = 0
const nextLayerId = () => 'layer' + ++_layerSeq

/**
 * Installs the per-layer thematic data/encoding API onto `layer`.
 *
 * Visual encoding CONFIG is read/written on `layer` (layer.encodings_).
 * Statistical DATA is always resolved through the shared `map` (datasets are
 * map-level and loaded once: map.statData_, map.statMeta_).
 *
 * For the legacy facade (the map is its own layer 0) `layer === map`, so every
 * reference resolves to the map exactly as it did before this refactor.
 *
 * This block is relocated VERBATIM (semantics-preserving) from createStatMap().
 *
 * @param {object} layer object the methods are installed on (a Layer, or the map for the facade)
 * @param {object} map   owns the shared data (statData_/statMeta_)
 */
export const attachThematicApi = function (layer, map) {
    layer.encodings_ = layer.encodings_ || {}

    const hasLoadedStatData = (key) => !!map.statData_?.[key]?.getArray?.()?.length
    const hasStatConfig = (key) => map.stat_?.[key] != null

    const inferImplicitFillStat = () => {
        // Common built-in channels that should not be auto-picked as choropleth fill defaults.
        const reserved = new Set(['default', 'color', 'size', 'v1', 'v2', 'v3'])

        const configured = Object.keys(map.stat_ || {}).filter((k) => !reserved.has(k) && hasStatConfig(k))
        if (configured.length === 1) return configured[0]

        const loaded = Object.keys(map.statData_ || {}).filter((k) => !reserved.has(k) && hasLoadedStatData(k))
        if (loaded.length === 1) return loaded[0]

        return undefined
    }

    const applyEncodingSideEffects = function (encodings) {
        const color = encodings?.color
        if (!color) return

        if (color.values) {
            layer.catColors_ = layer.catColors_ || {}
            if (Array.isArray(color.values)) {
                const statName = color.stat || layer.encoding('height')?.stat || layer.encoding('composition')?.stat || 'composition'
                const codes = color.categoryCodes || map.statMeta_[statName]?.categoryCodes || layer.statCodes_ || []
                codes.forEach((code, i) => {
                    if (color.values[i] != null) layer.catColors_[code] = color.values[i]
                })
            } else {
                layer.catColors_ = { ...layer.catColors_, ...color.values }
            }
        }

        if (color.labels) {
            layer.catLabels_ = layer.catLabels_ || {}
            if (Array.isArray(color.labels)) {
                const statName = color.stat || layer.encoding('height')?.stat || layer.encoding('composition')?.stat || 'composition'
                const codes = color.categoryCodes || map.statMeta_[statName]?.categoryCodes || layer.statCodes_ || []
                codes.forEach((code, i) => {
                    if (color.labels[i] != null) layer.catLabels_[code] = color.labels[i]
                })
            } else {
                layer.catLabels_ = { ...layer.catLabels_, ...color.labels }
            }
        }
    }

    // Setter returns the layer (which, for the facade, IS the map → preserves the
    // legacy fluent chain). When the MAP forwards this method in Phase 3, the
    // forwarder (forwardChainableMethod) substitutes the map as the return value.
    layer.encoding = function (channel, config) {
        if (!arguments.length) return layer.encodings_
        if (arguments.length === 1) {
            if (typeof channel === 'string' || channel instanceof String) return layer.encodings_[channel]
            layer.encodings_ = { ...layer.encodings_, ...channel }
            applyEncodingSideEffects(channel)
            return layer
        }
        layer.encodings_[channel] = config
        applyEncodingSideEffects({ [channel]: config })
        return layer
    }

    layer.getEncodingStat = (channel, fallback) => {
        const explicit = layer.encodings_[channel]?.stat
        if (explicit) return explicit

        if (channel === 'fill') {
            const fallbackKey = fallback || 'default'
            if (fallbackKey && (hasStatConfig(fallbackKey) || hasLoadedStatData(fallbackKey))) return fallbackKey

            // Convenience default for choropleths: if users define exactly one
            // custom stat dataset (e.g. statData('value').setData(...)) and do
            // not provide encoding('fill', ...), use that dataset for fill.
            const implicit = inferImplicitFillStat()
            if (implicit) return implicit
        }

        return fallback
    }

    layer.getEncodingStats = (channel, fallback) => {
        const e = layer.encodings_[channel]
        if (e?.stats) return e.stats
        if (e?.stat) return [e.stat]
        return fallback
    }

    layer.getEncodingStatKey = (channel, categoryCode, fallbackStat) => {
        const statName = layer.getEncodingStat(channel, fallbackStat || channel)
        if (!categoryCode) return statName
        return map.statMeta_[statName]?.statKeys?.[categoryCode] || statName
    }

    layer.getEncodingStatData = (channel, categoryCode, fallbackStat) => map.statData(layer.getEncodingStatKey(channel, categoryCode, fallbackStat))

    layer.getEncodingValue = (channel, regionId, categoryCode, fallbackStat) =>
        layer.getEncodingStatData(channel, categoryCode, fallbackStat)?.get(regionId)?.value

    layer.getEncodingUnitText = (channel, categoryCode, fallbackStat) => {
        const sd = layer.getEncodingStatData(channel, categoryCode, fallbackStat)
        return sd?.unitText?.() || layer.encodings_[channel]?.unitText || ''
    }

    // Forward stat and statData methods to the map for backwards compatibility.
    // Legacy tooltip functions expect layer.statData('size') to work.
    layer.stat = function (...args) {
        return map.stat(...args)
    }

    layer.statData = function (...args) {
        return map.statData(...args)
    }

    // noDataText is map-level state (set once via map.noDataText(...)), not per-layer.
    // Custom tooltip textFunctions are called as textFunction(region, layer) and, per the
    // documented API, read the "map"-like second argument's .noDataText_ directly - forward
    // it here so that still resolves instead of silently reading undefined.
    if (layer !== map) {
        Object.defineProperty(layer, 'noDataText_', {
            configurable: true,
            get: () => map.noDataText_,
        })
    }

    layer.legend = function (v) {
        if (!arguments.length) return layer.legend_

        if (v === false) {
            const legend = layer.legendObj_
            if (legend) {
                const legendSvg = select('#' + legend.svgId)
                if (legendSvg.size() > 0) {
                    legendSvg.selectAll('*').remove()
                }
            }
            if (map.svg) {
                map.svg()?.select('#em-legend-button').remove()
            }
            layer.legend_ = v
            return layer
        }
        layer.legend_ = v
        if (layer.legendObj_) {
            if (layer.updateClassification) layer.updateClassification()
            if (layer.updateStyle) layer.updateStyle()
            layer.legendObj_.update?.()
        }
        return layer
    }

    return layer
}

/** Returns/creates the DOM group a layer renders into (see plan §6.2). */
const getLayerGroup = function (layer) {
    const map = layer.map
    const svg = map.svg && map.svg()
    if (!svg) return null
    const zoomGroup = svg.select('#em-zoom-group-' + map.svgId_)

    // Base layers paint the shared region paths; their "group" is the zoom group.
    if (layer.role === 'base') return zoomGroup

    const id = 'em-layer-' + layer.id + '-' + map.svgId_
    let g = svg.select('#' + id)
    if (g.empty()) {
        g = zoomGroup
            .append('g')
            .attr('id', id)
            .attr('class', 'em-layer em-layer--' + layer.type)
        orderLayerGroups(map)
    }
    return g
}

/** Re-append overlay groups in stack order so paint order == layers_ order (bottom→top). */
const orderLayerGroups = function (map) {
    const svg = map.svg && map.svg()
    if (!svg) return
    const zoomNode = svg.select('#em-zoom-group-' + map.svgId_).node()
    if (!zoomNode) return
    map.layers_.forEach((l) => {
        if (l.role === 'base') return
        const node = svg.select('#em-layer-' + l.id + '-' + map.svgId_).node()
        if (node) zoomNode.appendChild(node) // appendChild moves the existing node to the end (top)
    })
}

/**
 * Real Layer factory. Used by map.addLayer() for REGISTERED types (Phase 3+).
 * In Phase 1 this is exercised only by the unit test (no type registers yet).
 *
 * @param {MapInstance} map
 * @param {LayerConfig} config
 * @returns {Layer}
 */
export const createLayer = function (map, config = {}) {
    const layer = {}
    // A thematic layer owns its encoding/rendering state, while geometry, SVG,
    // interaction and lifecycle services remain map-level. Inherit those services
    // so decorators can use the same public surface as legacy single-type maps.
    // Own layer properties always win and no map state is copied.
    const mapServices = {}
    Object.keys(map).forEach((key) => {
        Object.defineProperty(mapServices, key, {
            configurable: true,
            get: () => map[key],
            set(value) {
                Object.defineProperty(this, key, { configurable: true, enumerable: true, writable: true, value })
            },
        })
    })
    Object.setPrototypeOf(layer, mapServices)
    layer.map = map
    layer.isLayer = true
    layer.id = config.id || nextLayerId()
    layer.type = config.type
    layer.role = config.role || getRole(config.type)

    layer.encodings_ = {}
    layer.catColors_ = undefined
    layer.catLabels_ = undefined
    layer.statCodes_ = undefined
    layer.legend_ = config.legend
    layer.legendObj_ = undefined
    layer.tooltip_ = config.tooltip || {}
    layer.noDataFillStyle_ = config.noDataFillStyle || map.noDataFillStyle_ || '#ADADAD'

    // Abstract render methods — the type's decorate() overrides these in Phase 3+.
    layer.updateClassification = function () {
        console.log('Layer updateClassification not implemented (' + layer.type + ')')
        return layer
    }
    layer.updateStyle = function () {
        console.log('Layer updateStyle not implemented (' + layer.type + ')')
        return layer
    }
    layer.getLegendConstructor = function () {
        console.log('Layer getLegendConstructor not implemented (' + layer.type + ')')
        return Legend.legend
    }

    layer.group = function () {
        return getLayerGroup(layer)
    }

    attachThematicApi(layer, map)

    // Seed encodings from config.
    if (config.encoding) layer.encoding(config.encoding)

    return layer
}

/**
 * Tags the MAP as its own layer 0 (the legacy facade) and installs the thematic
 * API on it. Called once by createStatMap. Behaviour-identical to the pre-refactor
 * inline definitions, because layer === map.
 *
 * @param {MapInstance} map
 */
export const makeMapSelfLayer = function (map) {
    map.isLayer = true
    map.layerId_ = map.layerId_ || 'layer-self-' + map.svgId_
    map.role = map.role || getRole(map._mapType)
    // For the facade, group() is simply the zoom group (legacy types manage their
    // own DOM the old way); defined so activeLayer().group() never throws.
    if (!map.group) {
        map.group = function () {
            const svg = map.svg && map.svg()
            return svg ? svg.select('#em-zoom-group-' + map.svgId_) : null
        }
    }
    attachThematicApi(map, map)
    return map
}

/**
 * Forwards plain thematic FIELDS on the map to its active layer, so legacy callers
 * reading map.<field> transparently hit map.activeLayer().<field>. Render methods
 * (updateClassification/updateStyle/getLegendConstructor) forward the same way.
 *
 * Used in Phase 3+ ONLY (a migrated type's factory). Never call it on a facade map
 * (it would be self-referential).
 */
export const forwardFieldsToActiveLayer = function (map, fieldNames) {
    for (const f of fieldNames) {
        Object.defineProperty(map, f, {
            configurable: true,
            get() {
                const l = map.activeLayer()
                if (!l) return undefined
                // Read the layer's OWN value only. A layer's prototype (mapServices, set up in
                // createLayer) falls back to reading map[key] for any key it doesn't own itself -
                // and this property IS map[f], since this getter is what's redefining it. A layer
                // that never set its own `f` (e.g. no ranked bar chart configured) would otherwise
                // bounce straight back into this same getter and recurse until the stack overflows.
                return Object.prototype.hasOwnProperty.call(l, f) ? l[f] : undefined
            },
            set(v) {
                const l = map.activeLayer()
                if (l) l[f] = v
            },
        })
    }
}

/**
 * Forwards a fluent getter/setter METHOD (e.g. `encoding`) to the active layer while
 * keeping the MAP as the return value on a set, so map-level chaining is preserved:
 *   map.encoding('fill', cfg).title(...)  // still returns the map
 * Getter calls (0 args, or a single string arg) return the underlying value.
 */
export const forwardChainableMethod = function (map, name) {
    const originalMapMethod = typeof map[name] === 'function' ? map[name].bind(map) : null

    Object.defineProperty(map, name, {
        configurable: true,
        get() {
            return function (...args) {
                const layer = map.activeLayer()
                // Facade maps are their own active layer. In that case, calling layer[name]
                // would recurse through this forwarding getter. Likewise, only use layer[name]
                // when the layer OWNS it: otherwise the lookup falls through to the layer's
                // mapServices prototype (see createLayer), which reads map[name] - i.e. this
                // very forwarding wrapper - and would call itself forever.
                const layerMethod =
                    layer && layer !== map && Object.prototype.hasOwnProperty.call(layer, name) && typeof layer[name] === 'function'
                        ? layer[name].bind(layer)
                        : null
                const targetMethod = layerMethod || originalMapMethod

                if (!targetMethod) {
                    return args.length === 0 ? undefined : map
                }

                const r = targetMethod(...args)
                let isGetter = args.length === 0
                if (args.length === 1 && (name === 'encoding' || name === 'stat' || name === 'statData')) {
                    if (typeof args[0] === 'string' || args[0] instanceof String) {
                        isGetter = true
                    }
                }
                return isGetter ? r : map
            }
        },
    })
}
